import React from "react";
import axios from "axios";

import "./SlaAging.css";

const formatCurrency = value => new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
}).format(Number(value) || 0);

const formatDateTime = value => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      }).format(date);
};

const formatAge = minutes => {
  const totalMinutes = Number(minutes) || 0;
  if (totalMinutes >= 1440) return `${Math.floor(totalMinutes / 1440)} hari ${Math.floor((totalMinutes % 1440) / 60)} jam`;
  if (totalMinutes >= 60) return `${Math.floor(totalMinutes / 60)} jam ${totalMinutes % 60} menit`;
  return `${totalMinutes} menit`;
};

const severityMeta = {
  overdue: { label: "Overdue", icon: "fas fa-exclamation-circle" },
  due_soon: { label: "Mendekati SLA", icon: "fas fa-clock" },
  within_sla: { label: "Dalam SLA", icon: "fas fa-check-circle" },
};

class SlaAging extends React.Component {
  state = {
    data: null,
    loading: true,
    error: "",
    source: "",
    severity: "",
    search: "",
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const response = await axios.get(global.variable.LINK_API + "management/sla-aging", {
        ...global.variable.HEADERS_BEARER,
        cancelKey: "sla-aging-report",
      });
      this.setState({ data: response.data });
    } catch (error) {
      if (axios.isCancel(error)) return;
      this.setState({ error: error?.response?.data?.message || "Data SLA & Aging belum dapat dimuat." });
    } finally {
      this.setState({ loading: false });
    }
  };

  openItem = item => {
    if (item.entity_type !== "invoice") return;
    const basePath = item.source === "bursakopi" ? "/bursa-kopi/transactiondetail/" : "/transactiondetail/";
    this.props.history.push(basePath + encodeURIComponent(item.reference_id));
  };

  render() {
    const { data, loading, error, source, severity, search } = this.state;
    const summary = data?.summary || {};
    const sources = data?.sources || [];
    const buckets = data?.aging_buckets || [];
    const maxBucket = Math.max(...buckets.map(bucket => Number(bucket.total) || 0), 1);
    const normalizedSearch = search.trim().toLowerCase();
    const items = (data?.items || []).filter(item => {
      if (source && item.source !== source) return false;
      if (severity && item.severity !== severity) return false;
      if (!normalizedSearch) return true;
      return [item.reference_id, item.merchant_name, item.status_name, item.source_name]
        .some(value => String(value || "").toLowerCase().includes(normalizedSearch));
    });

    if (loading && !data) {
      return <div className="insight-state"><span className="insight-state__loader" /><h2>Menyiapkan SLA & Aging</h2><p>Menghitung umur status dari seluruh sumber data.</p></div>;
    }

    if (error && !data) {
      return <div className="insight-state insight-state--error"><span><i className="fas fa-exclamation-triangle" /></span><h2>SLA & Aging tidak dapat dimuat</h2><p>{error}</p><button type="button" onClick={this.load}>Coba lagi</button></div>;
    }

    return (
      <section className="sla-page">
        <header className="sla-hero">
          <div><span><i className="fas fa-stopwatch" /> OPERATIONAL CONTROL</span><h1>SLA & Aging</h1><p>Pantau invoice dan payout yang tertahan berdasarkan waktu masuk ke status aktif.</p></div>
          <div className="sla-hero__actions"><span><i className="fas fa-eye" /> READ-ONLY</span><button type="button" onClick={this.load} disabled={loading}><i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Perbarui</button></div>
        </header>

        {error && <div className="sla-inline-alert"><i className="fas fa-info-circle" /> {error}</div>}

        <div className="sla-kpis">
          <article className="sla-kpi sla-kpi--danger"><span><i className="fas fa-exclamation-triangle" /></span><div><small>Overdue</small><strong>{Number(summary.overdue || 0).toLocaleString("id-ID")}</strong><p>Melewati batas SLA</p></div></article>
          <article className="sla-kpi sla-kpi--warning"><span><i className="fas fa-hourglass-half" /></span><div><small>Mendekati SLA</small><strong>{Number(summary.due_soon || 0).toLocaleString("id-ID")}</strong><p>Pemakaian SLA 70–100%</p></div></article>
          <article className="sla-kpi sla-kpi--success"><span><i className="fas fa-shield-alt" /></span><div><small>SLA compliance</small><strong>{Number(summary.compliance_rate || 0).toLocaleString("id-ID")}%</strong><p>{summary.available_sources || 0}/{summary.total_sources || 0} sumber aktif</p></div></article>
          <article className="sla-kpi sla-kpi--info"><span><i className="far fa-clock" /></span><div><small>Rata-rata aging</small><strong>{Number(summary.average_age_hours || 0).toLocaleString("id-ID")} jam</strong><p>{Number(summary.total_open || 0).toLocaleString("id-ID")} item terbuka</p></div></article>
        </div>

        <div className="sla-source-grid">
          {sources.map(item => (
            <article className={`sla-source ${item.available ? "" : "sla-source--offline"}`} key={item.key}>
              <div><span className={`sla-source__mark sla-source__mark--${item.key}`}><i className={item.key === "payout" ? "fas fa-hand-holding-usd" : item.key === "bursakopi" ? "fas fa-mug-hot" : item.key === "scm360" ? "fas fa-project-diagram" : "fas fa-shopping-bag"} /></span><div><small>SUMBER DATA</small><h3>{item.name}</h3></div></div>
              <span className={`sla-source__availability sla-source__availability--${item.available ? "online" : "offline"}`}><i />{item.available ? "Aktif" : "Tidak tersedia"}</span>
              {item.available ? <div className="sla-source__metrics"><div><span>Open</span><strong>{item.total_open}</strong></div><div><span>Overdue</span><strong>{item.overdue}</strong></div><div><span>Compliance</span><strong>{item.compliance_rate}%</strong></div></div> : <p>{item.status_note}</p>}
            </article>
          ))}
        </div>

        <div className="sla-overview-grid">
          <article className="sla-panel">
            <header><div><span>DISTRIBUSI UMUR</span><h2>Aging bucket</h2><p>Jumlah item berdasarkan lama berada pada status aktif.</p></div></header>
            <div className="sla-buckets">{buckets.map(bucket => <div key={bucket.key}><div><span>{bucket.label}</span><strong>{Number(bucket.total).toLocaleString("id-ID")}</strong></div><span><i style={{ width: `${Math.max(3, (Number(bucket.total) / maxBucket) * 100)}%` }} /></span></div>)}</div>
          </article>
          <article className="sla-panel sla-panel--policy">
            <header><div><span>KEBIJAKAN</span><h2>Cara membaca SLA</h2><p>Indikator dihitung otomatis tanpa mengubah data transaksi.</p></div></header>
            <div className="sla-policy-list"><div><i className="fas fa-check" /><span><strong>Dalam SLA</strong><small>Kurang dari 70% batas waktu.</small></span></div><div><i className="fas fa-clock" /><span><strong>Mendekati SLA</strong><small>Sudah menggunakan 70–100% batas waktu.</small></span></div><div><i className="fas fa-exclamation" /><span><strong>Overdue</strong><small>Umur status telah melewati target.</small></span></div></div>
          </article>
        </div>

        <article className="sla-panel sla-worklist">
          <header className="sla-worklist__header"><div><span>PRIORITY WORKLIST</span><h2>Item yang membutuhkan perhatian</h2><p>Urutan berdasarkan tingkat pelanggaran dan umur status.</p></div><strong>{items.length} item ditampilkan</strong></header>
          <div className="sla-filters">
            <div><i className="fas fa-search" /><input type="search" value={search} onChange={event => this.setState({ search: event.target.value })} placeholder="Cari invoice, merchant, atau status" /></div>
            <select value={source} onChange={event => this.setState({ source: event.target.value })}><option value="">Semua sumber</option>{sources.map(item => <option value={item.key} key={item.key}>{item.name}</option>)}</select>
            <select value={severity} onChange={event => this.setState({ severity: event.target.value })}><option value="">Semua tingkat</option><option value="overdue">Overdue</option><option value="due_soon">Mendekati SLA</option><option value="within_sla">Dalam SLA</option></select>
          </div>
          <div className="sla-table-wrap"><table className="sla-table"><thead><tr><th>Referensi</th><th>Sumber</th><th>Status aktif</th><th>Merchant</th><th>Umur status</th><th>Target</th><th>Nilai</th><th>SLA</th></tr></thead><tbody>{items.length ? items.map(item => { const meta = severityMeta[item.severity]; return <tr key={`${item.source}-${item.entity_type}-${item.reference_id}`} onClick={() => this.openItem(item)} className={item.entity_type === "invoice" ? "sla-table__clickable" : ""}><td><strong>{item.reference_id}</strong><small>{item.entity_type === "invoice" ? "Invoice" : "Payout"}</small></td><td>{item.source_name}</td><td><strong>{item.status_name}</strong><small>Sejak {formatDateTime(item.status_since)}</small></td><td>{item.merchant_name}</td><td><strong>{formatAge(item.age_minutes)}</strong></td><td>{item.target_hours} jam</td><td>{formatCurrency(item.amount)}</td><td><span className={`sla-badge sla-badge--${item.severity}`}><i className={meta.icon} />{meta.label}</span></td></tr>; }) : <tr><td colSpan="8"><div className="sla-empty"><i className="fas fa-check-circle" /><strong>Tidak ada item sesuai filter</strong><span>Ubah filter untuk melihat antrean lainnya.</span></div></td></tr>}</tbody></table></div>
        </article>
      </section>
    );
  }
}

export default SlaAging;
